import Phaser from 'phaser';
import { WorldNumber } from '../levels';

export type WorldTabState = 'current' | 'unlocked' | 'locked';

const FILL_CURRENT = 0x121a32;
const FILL_UNLOCKED = 0x0a0f1f;
const FILL_LOCKED = 0x1f2937;
const BORDER_CURRENT = 0xffd24a;
const BORDER_UNLOCKED = 0x64748b;
const BORDER_LOCKED = 0x374151;
const GLYPH_COLOR = 0xe5e7eb;
const GLYPH_DIM = 0x6b7280;

export function drawWorldTab(
  g: Phaser.GameObjects.Graphics,
  world: WorldNumber,
  radius: number,
  state: WorldTabState,
) {
  g.clear();
  const fill = state === 'current' ? FILL_CURRENT : state === 'unlocked' ? FILL_UNLOCKED : FILL_LOCKED;
  const border = state === 'current' ? BORDER_CURRENT : state === 'unlocked' ? BORDER_UNLOCKED : BORDER_LOCKED;
  g.fillStyle(fill, 1);
  g.lineStyle(state === 'current' ? 3 : 2, border, 1);
  g.fillCircle(0, 0, radius);
  g.strokeCircle(0, 0, radius);

  const color = state === 'locked' ? GLYPH_DIM : GLYPH_COLOR;
  switch (world) {
    case 1:
      drawNycGlyph(g, radius, color);
      return;
    case 2:
      drawAsgardGlyph(g, radius, color);
      return;
    case 3:
      drawTowerGlyph(g, radius, color);
      return;
    case 4:
      drawPantherGlyph(g, radius, color);
      return;
    case 5:
      drawCosmicGlyph(g, radius, color);
      return;
    case 6:
      drawRiftGlyph(g, radius, color);
      return;
    case 7:
      drawSpiderGlyph(g, radius, color);
      return;
    case 8:
      drawMutantGlyph(g, radius, color);
      return;
    case 9:
      drawKnowhereGlyph(g, radius, color);
      return;
    case 10:
      drawAngelGroveGlyph(g, radius, color);
      return;
    case 11:
      drawMoonPalaceGlyph(g, radius, color);
      return;
    case 12:
      drawZordBattleGlyph(g, radius, color);
      return;
    case 13:
      drawHellsKitchenGlyph(g, radius, color);
      return;
    case 14:
      drawGalactusGlyph(g, radius, color);
      return;
  }
}

function drawHellsKitchenGlyph(
  g: Phaser.GameObjects.Graphics,
  radius: number,
  color: number,
) {
  const r = radius * 0.7;
  g.fillStyle(color, 1);
  g.fillRect(-r * 0.85, -r * 0.1, r * 1.7, r * 0.9);
  g.fillRect(-r * 0.2, -r * 0.75, r * 0.4, r * 0.7);
  g.fillStyle(0xef4444, 1);
  g.fillCircle(0, -r * 0.3, r * 0.22);
  g.fillStyle(0xfacc15, 0.95);
  g.fillRect(-r * 0.6, r * 0.35, r * 0.18, r * 0.22);
  g.fillRect(r * 0.42, r * 0.35, r * 0.18, r * 0.22);
}

function drawGalactusGlyph(
  g: Phaser.GameObjects.Graphics,
  radius: number,
  color: number,
) {
  const r = radius * 0.6;
  g.fillStyle(color, 1);
  g.fillCircle(0, r * 0.25, r * 0.55);
  g.fillTriangle(-r * 0.75, -r * 0.1, 0, -r * 0.95, r * 0.75, -r * 0.1);
  g.fillStyle(0xe879f9, 1);
  g.fillCircle(-r * 0.2, -r * 0.1, r * 0.1);
  g.fillCircle(r * 0.2, -r * 0.1, r * 0.1);
  g.fillStyle(0xfacc15, 0.9);
  g.fillCircle(0, r * 0.25, r * 0.18);
}

function drawAngelGroveGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.65;
  g.fillStyle(0xef4444, 1);
  g.fillTriangle(-r * 0.9, r * 0.3, 0, -r * 0.9, r * 0.9, r * 0.3);
  g.fillStyle(color, 1);
  g.fillRect(-r * 0.15, -r * 0.55, r * 0.3, r * 0.85);
  g.fillRect(-r * 0.5, -r * 0.1, r * 1, r * 0.3);
  g.fillStyle(0xfde047, 1);
  g.fillCircle(0, -r * 0.1, r * 0.14);
}

function drawMoonPalaceGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.65;
  g.fillStyle(color, 1);
  g.lineStyle(2, 0x0a0f1f, 1);
  g.fillCircle(0, 0, r * 0.9);
  g.fillStyle(0x0a0f1f, 1);
  g.fillCircle(r * 0.35, -r * 0.35, r * 0.15);
  g.fillCircle(-r * 0.25, r * 0.2, r * 0.12);
  g.fillStyle(0xef4444, 1);
  const stroke = Math.max(3, radius * 0.11);
  g.lineStyle(stroke, 0xef4444, 1);
  g.beginPath();
  g.moveTo(-r * 0.45, -r * 0.45);
  g.lineTo(r * 0.45, -r * 0.45);
  g.lineTo(-r * 0.45, r * 0.45);
  g.lineTo(r * 0.45, r * 0.45);
  g.strokePath();
}

function drawZordBattleGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.62;
  g.fillStyle(color, 1);
  g.fillRect(-r * 0.7, -r * 0.2, r * 1.4, r * 0.85);
  g.fillStyle(0xfde047, 1);
  g.fillRect(-r * 0.5, -r * 0.75, r * 1, r * 0.5);
  g.fillStyle(0xef4444, 1);
  g.fillCircle(-r * 0.2, -r * 0.5, r * 0.1);
  g.fillCircle(r * 0.2, -r * 0.5, r * 0.1);
  g.fillStyle(color, 1);
  g.fillRect(-r * 0.85, r * 0.3, r * 0.25, r * 0.45);
  g.fillRect(r * 0.6, r * 0.3, r * 0.25, r * 0.45);
}

function drawKnowhereGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.58;
  g.fillStyle(color, 1);
  g.lineStyle(2, 0x0a0f1f, 1);
  g.fillEllipse(0, 0, r * 2, r * 1.6);
  g.fillStyle(0x60a5fa, 1);
  const eyeR = r * 0.24;
  g.fillCircle(-r * 0.4, -r * 0.15, eyeR);
  g.fillCircle(r * 0.4, -r * 0.15, eyeR);
}

function drawMutantGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.7;
  g.lineStyle(Math.max(4, radius * 0.16), color, 1);
  g.beginPath();
  g.moveTo(-r, -r);
  g.lineTo(r, r);
  g.moveTo(r, -r);
  g.lineTo(-r, r);
  g.strokePath();
  g.fillStyle(0xef4444, 1);
  g.fillCircle(0, 0, r * 0.22);
}

function drawSpiderGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const bodyR = radius * 0.32;
  g.fillStyle(color, 1);
  g.fillCircle(0, 0, bodyR);
  g.fillCircle(0, -bodyR * 0.8, bodyR * 0.5);
  g.lineStyle(Math.max(2, radius * 0.07), color, 1);
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < 3; i++) {
      const angleOffset = -0.4 + i * 0.4;
      const legLen = bodyR * (1.5 + i * 0.25);
      const tipX = side * legLen;
      const tipY = angleOffset * legLen * 0.8;
      g.beginPath();
      g.moveTo(side * bodyR * 0.5, angleOffset * bodyR * 0.3);
      g.lineTo(side * legLen * 0.6, angleOffset * legLen * 0.4);
      g.lineTo(tipX, tipY);
      g.strokePath();
    }
  }
}

function drawRiftGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.55;
  g.lineStyle(3, 0xfbbf24, 1);
  g.fillStyle(color, 1);
  g.beginPath();
  const segments = 24;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const a = t * Math.PI * 2;
    const wobble = 1 + Math.sin(a * 3) * 0.25;
    const x = Math.cos(a) * r * wobble;
    const y = Math.sin(a) * r * wobble * 0.75;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
  g.strokePath();
  g.fillStyle(0xf97316, 1);
  g.fillCircle(0, 0, r * 0.28);
}

function drawCosmicGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const r = radius * 0.48;
  g.fillStyle(color, 1);
  g.fillCircle(0, 0, r);
  g.lineStyle(2, 0xfbbf24, 1);
  g.beginPath();
  g.arc(0, 0, r * 1.45, Math.PI * 0.15, Math.PI * 0.85, false);
  g.strokePath();
  g.beginPath();
  g.arc(0, 0, r * 1.68, Math.PI * 0.1, Math.PI * 0.9, false);
  g.strokePath();
}

function drawNycGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  g.fillStyle(color, 1);
  const baseY = radius * 0.5;
  const bars = [0.25, 0.55, 0.35, 0.7, 0.4];
  const w = radius * 1.4;
  const x0 = -w / 2;
  bars.forEach((h, i) => {
    const bw = w / bars.length;
    g.fillRect(x0 + i * bw, baseY - h * radius, bw - 2, h * radius);
  });
}

function drawAsgardGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  g.lineStyle(3, color, 1);
  const archW = radius * 1.2;
  const archH = radius * 1.0;
  g.beginPath();
  g.arc(0, radius * 0.25, archW / 2, Math.PI, 0, false);
  g.strokePath();
  g.fillStyle(color, 1);
  g.fillRect(-archW / 2 - 1, radius * 0.25, 3, archH * 0.6);
  g.fillRect(archW / 2 - 2, radius * 0.25, 3, archH * 0.6);
  g.fillRect(-archW / 2, radius * 0.75, archW, 3);
}

function drawTowerGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  const towerW = radius * 0.5;
  const towerH = radius * 1.3;
  const towerX = -towerW / 2;
  const towerY = radius * 0.55;
  g.fillStyle(color, 1);
  g.fillRect(towerX, towerY - towerH, towerW, towerH);

  g.fillStyle(0xfbbf24, 1);
  const aH = towerH * 0.36;
  const aW = towerW * 1.8;
  const aCx = 0;
  const aCy = towerY - towerH * 0.55;
  g.lineStyle(Math.max(2, radius * 0.08), 0xfbbf24, 1);
  g.beginPath();
  g.moveTo(aCx - aW / 2, aCy + aH / 2);
  g.lineTo(aCx, aCy - aH / 2);
  g.lineTo(aCx + aW / 2, aCy + aH / 2);
  g.strokePath();
  g.beginPath();
  g.moveTo(aCx - aW * 0.25, aCy);
  g.lineTo(aCx + aW * 0.25, aCy);
  g.strokePath();
}

function drawPantherGlyph(g: Phaser.GameObjects.Graphics, radius: number, color: number) {
  g.fillStyle(color, 1);
  const r = radius * 0.6;
  g.fillCircle(0, 0, r);
  g.fillStyle(0x0a0f1f, 1);
  g.fillCircle(-r * 0.32, -r * 0.12, r * 0.18);
  g.fillCircle(r * 0.32, -r * 0.12, r * 0.18);
  g.fillStyle(color, 1);
  g.fillTriangle(-r * 0.75, -r * 0.55, -r * 0.45, -r * 0.95, -r * 0.25, -r * 0.55);
  g.fillTriangle(r * 0.25, -r * 0.55, r * 0.45, -r * 0.95, r * 0.75, -r * 0.55);
}
